import { usePathname, useSearchParams } from 'next/navigation';
import { useParentHistoryParams } from 'hooks/useParentHistoryParams';
import {
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
  forwardRef,
  AnchorHTMLAttributes,
  MouseEvent,
} from 'react';
import clsx from 'clsx';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { Link as MuiLink, LinkProps as MuiLinkProps } from '@mildang/mui';
// import useCustomRouter from 'hooks/useCustomRouter';

import useShowWarning from 'hooks/useShowWarning';
import { useSyncStudyStore } from 'hooks/syncStudyStore';
import { useLingui } from '@lingui/react';
interface NextLinkComposedProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>,
    Omit<NextLinkProps, 'href' | 'as' | 'onClick' | 'onMouseEnter' | 'onTouchStart'> {
  to: NextLinkProps['href'];
  linkAs?: NextLinkProps['as'];
  href?: NextLinkProps['href'];
  isExternal?: boolean;
}

// eslint-disable-next-line react/display-name
export const NextLinkComposed = forwardRef<HTMLAnchorElement, NextLinkComposedProps>((props, ref) => {
  const { to, linkAs, href, replace, scroll, passHref, shallow, prefetch, locale, isExternal, ...other } =
    props;

  return (
    <NextLink
      href={to}
      prefetch={prefetch}
      as={linkAs}
      replace={replace}
      scroll={scroll}
      shallow={shallow}
      passHref={passHref}
      locale={locale}
      ref={ref}
      {...other}
    ></NextLink>
  );
});

export type LinkProps = {
  activeClassName?: string;
  as?: NextLinkProps['as'];
  href: NextLinkProps['href'];
  noLinkStyle?: boolean;
  parentPush?: boolean;
} & Omit<NextLinkComposedProps, 'to' | 'linkAs' | 'href'> &
  Omit<MuiLinkProps, 'href'>;

// A styled version of the Next.js Link component:
// https://nextjs.org/docs/#with-link
const LinkForwardRef: ForwardRefExoticComponent<
  PropsWithoutRef<LinkProps> & RefAttributes<HTMLAnchorElement>
  // eslint-disable-next-line react/display-name
> = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const {
    activeClassName = 'active',
    as: linkAs,
    className: classNameProps,
    href = '',
    noLinkStyle,
    parentPush,
    role, // Link don't have roles.
    isExternal: isExternalProp,
    onClick,
    ...other
  } = props;
  const sync = useSyncStudyStore((state) => state.sync);
  const { i18n } = useLingui();

  const pathname = typeof href === 'string' ? href : href.pathname;
  const showWarning = useShowWarning();

  const routerPathname = usePathname();
  const searchParams = useSearchParams();

  const className = clsx(classNameProps, {
    [activeClassName]: routerPathname === pathname && activeClassName,
  });

  const { pushParentPath } = useParentHistoryParams();

  function onMouseDown(e: MouseEvent<HTMLAnchorElement>) {
    if (parentPush) {
      pushParentPath(`${routerPathname}?${searchParams.toString()}`);
    }

    other?.onMouseDown?.(e);
  }

  const handleLinkClick = (e: MouseEvent) => {
    onClick?.(e as unknown as MouseEvent<HTMLAnchorElement>);
    if (sync) {
      e.preventDefault();
      return false;
    }
    let isConfirm = false;

    if (showWarning) {
      isConfirm = window.confirm(
        i18n.t({
          message: '지금 나가면 입력한 내용이 저장되지 않습니다. 페이지를 나갈까요?',
          id: '지금 나가면 입력한 내용이 저장되지 않습니다. 페이지를 나갈까요?',
        }),
      );
    }

    if (showWarning && !isConfirm) {
      e.preventDefault();
    }
  };

  const isExternal =
    (typeof href === 'string' && (href.indexOf('http') === 0 || href.indexOf('mailto:') === 0)) ||
    isExternalProp;

  if (isExternal) {
    if (noLinkStyle) {
      return <a className={className} href={href as string} ref={ref as any} {...other} />;
    }

    return <MuiLink className={className} href={href as string} ref={ref} {...other} />;
  }

  if (noLinkStyle) {
    return (
      <NextLinkComposed
        className={className}
        onClick={handleLinkClick}
        ref={ref as any}
        to={href}
        {...other}
        onMouseDown={onMouseDown}
        style={sync ? { cursor: 'not-allowed', pointerEvents: 'none' } : {}}
      />
    );
  }

  return (
    <MuiLink
      component={NextLinkComposed}
      onClick={handleLinkClick}
      linkAs={linkAs}
      className={className}
      ref={ref}
      to={href}
      {...other}
      onMouseDown={onMouseDown}
      style={sync ? { cursor: 'not-allowed', pointerEvents: 'none' } : {}}
    />
  );
});

export default LinkForwardRef;
